import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Skeleton,
    Collapse,
    ButtonBase,
} from "@mui/material";
import { Search, ExpandMore, Close } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import { TIERS } from "../../constants/membership";
import LobbySelectableRow from "./LobbySelectableRow";
import PremiumOnlyOverlay from "./PremiumOnlyOverlay";
import { shouldShowPremiumMatchFiltersPrompt } from "./premiumMatchFiltersGate";
import SearchEmptyState from "../common/SearchEmptyState";
import MMSegmentedControl from "./MMSegmentedControl";
import {
    COUNTRY_FILTER_MODE_OPTIONS,
    COUNTRY_FILTER_MODES,
    getSameRegionCountryCodes,
    groupCountriesByContinent,
    isSameRegionSelection,
    resolveCountryFilterMode,
    resolvePlayerCountryPresentation,
    sameCodeSet,
} from "./countryFilterGroups";
import {
    MOBILE_NAV_MQ,
    SETTINGS_CONTENT_COLUMN_SX,
    SETTINGS_SECTION_DESC_SX,
    SETTINGS_SECTION_HEADER_SX,
    SETTINGS_SECTION_PAD_SX,
    SETTINGS_SECTION_TITLE_SX,
} from "./lobbySettingsStyles";

function selectedCodesFromOptions(options) {
    return Array.isArray(options.play_with_countries_list)
        ? options.play_with_countries_list
        : [];
}

const countrySearchFieldSx = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
        backgroundColor: colors.background.surface,
        borderRadius: "10px",
        transition: "border-color 0.12s ease",
        "& fieldset": {
            borderColor: colors.border.default,
            borderWidth: "1px",
        },
        "&:hover fieldset": {
            borderColor: alpha(colors.primary.main, 0.45),
        },
        "&.Mui-focused fieldset": {
            borderColor: alpha(colors.primary.main, 0.55),
            borderWidth: "1px",
        },
    },
    "& .MuiOutlinedInput-input": {
        color: colors.text.primary,
        fontFamily: "inherit",
        fontSize: "0.88rem",
        padding: "0.7rem 0.95rem",
        [MOBILE_NAV_MQ]: {
            fontSize: "0.8rem",
            padding: "0.6rem 0.8rem",
        },
    },
    "& .MuiOutlinedInput-input.MuiInputBase-inputAdornedStart": {
        paddingLeft: 0,
    },
    "& .MuiInputAdornment-root": {
        marginRight: "0.65rem",
        marginLeft: 0,
        color: colors.text.secondary,
    },
    "& .MuiOutlinedInput-input::placeholder": {
        color: colors.text.muted,
        opacity: 1,
    },
};

const COUNTRY_SEARCH_MAX_CHARS = 22;
const COUNTRY_SKELETON_COUNT = 6;
const countrySkeletonBg = { bgcolor: alpha(colors.text.primary, 0.08) };

function CountryFlag({ src }) {
    if (!src) {
        return (
            <Box
                sx={{
                    width: 22,
                    height: 16,
                    borderRadius: "3px",
                    backgroundColor: alpha(colors.text.primary, 0.1),
                    flexShrink: 0,
                }}
            />
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt=""
            sx={{
                width: 22,
                height: 16,
                borderRadius: "3px",
                objectFit: "cover",
                border: `1px solid ${alpha(colors.text.primary, 0.12)}`,
                flexShrink: 0,
            }}
        />
    );
}

function CountryFilterRowSkeleton() {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.45rem",
                px: "0.55rem",
                height: "2.85rem",
                borderRadius: "0.5rem",
                backgroundColor: alpha(colors.background.card, 0.3),
                border: `solid 1px ${colors.border.default}`,
            }}
        >
            <Skeleton variant="rounded" width={22} height={16} sx={countrySkeletonBg} />
            <Skeleton variant="text" sx={{ ...countrySkeletonBg, flex: 1, height: "1rem" }} />
            <Skeleton
                variant="circular"
                width={16}
                height={16}
                sx={countrySkeletonBg}
            />
        </Box>
    );
}

export default function CountryFilterSection({
    matchmakingOptions = {},
    onMatchmakingOptionChange,
    onLeaderMatchmakingUpdate,
    canEditLobbySettings = false,
    currentLobbyCode = null,
    isMatchmakingEditingDisabled = false,
    membershipTier = TIERS.FREE,
    settingsResetVersion = 0,
    hidePremiumOverlay = false,
    playerCountry = null,
}) {
    const basePath = utils.getEdgeBasePath();
    const [selectedCodes, setSelectedCodes] = useState(() =>
        selectedCodesFromOptions(matchmakingOptions),
    );
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [countriesFetchAttempted, setCountriesFetchAttempted] =
        useState(false);
    const [countrySearch, setCountrySearch] = useState("");
    const [expandedContinents, setExpandedContinents] = useState({});
    const lastLobbyCode = useRef(currentLobbyCode);
    const lastOptionsRef = useRef(matchmakingOptions);
    const lastSettingsResetVersionRef = useRef(settingsResetVersion);
    const lastCustomCodesRef = useRef(selectedCodesFromOptions(matchmakingOptions));
    const regionCodes = useMemo(
        () => getSameRegionCountryCodes(playerCountry, countries),
        [playerCountry, countries],
    );
    const [mode, setMode] = useState(() =>
        resolveCountryFilterMode(
            matchmakingOptions,
            getSameRegionCountryCodes(playerCountry, []),
        ),
    );

    useEffect(() => {
        const lobbyChanged = lastLobbyCode.current !== currentLobbyCode;
        const optionsChanged = lastOptionsRef.current !== matchmakingOptions;

        if (lobbyChanged || optionsChanged) {
            const nextCodes = selectedCodesFromOptions(matchmakingOptions);
            setSelectedCodes(nextCodes);
            setMode((currentMode) => {
                const resolved = resolveCountryFilterMode(
                    matchmakingOptions,
                    regionCodes,
                );
                if (
                    currentMode === COUNTRY_FILTER_MODES.CUSTOM &&
                    resolved === COUNTRY_FILTER_MODES.ANY &&
                    nextCodes.length === 0
                ) {
                    return COUNTRY_FILTER_MODES.CUSTOM;
                }
                return resolved;
            });
            lastLobbyCode.current = currentLobbyCode;
            lastOptionsRef.current = matchmakingOptions;
        }
    }, [matchmakingOptions, currentLobbyCode, regionCodes]);

    useEffect(() => {
        if (lastSettingsResetVersionRef.current === settingsResetVersion) {
            return;
        }
        const nextCodes = selectedCodesFromOptions(matchmakingOptions);
        setSelectedCodes(nextCodes);
        setMode(resolveCountryFilterMode(matchmakingOptions, regionCodes));
        lastSettingsResetVersionRef.current = settingsResetVersion;
    }, [settingsResetVersion, matchmakingOptions, regionCodes]);

    useEffect(() => {
        if (countriesFetchAttempted || countriesLoading) return;
        setCountriesFetchAttempted(true);
        setCountriesLoading(true);
        fetch(`${basePath}/api/v1/lobby/countries`)
            .then((r) => r.json())
            .then((data) => {
                if (data?.ok && Array.isArray(data.countries)) {
                    setCountries(data.countries);
                }
            })
            .catch(() => {})
            .finally(() => setCountriesLoading(false));
    }, [countriesFetchAttempted, countriesLoading, basePath]);

    useEffect(() => {
        if (isMatchmakingEditingDisabled) {
            return;
        }
        if (mode !== COUNTRY_FILTER_MODES.REGION || countries.length === 0) {
            return;
        }
        if (regionCodes.length === 0) {
            return;
        }
        if (sameCodeSet(selectedCodes, regionCodes)) {
            return;
        }
        applyCodes(COUNTRY_FILTER_MODES.REGION, regionCodes);
    }, [
        isMatchmakingEditingDisabled,
        mode,
        regionCodes,
        countries,
        selectedCodes,
    ]);

    const dispatchCountryFilterUpdate = (enabled, codes) => {
        const settingData = {
            name: "play_with_countries",
            enabled,
            hasSlider: false,
            listValue: codes,
        };
        if (
            canEditLobbySettings &&
            currentLobbyCode &&
            onLeaderMatchmakingUpdate
        ) {
            onLeaderMatchmakingUpdate(settingData);
        } else if (onMatchmakingOptionChange) {
            onMatchmakingOptionChange(settingData);
        }
    };

    const applyCodes = (nextMode, nextCodes) => {
        setMode(nextMode);
        setSelectedCodes(nextCodes);
        if (nextMode === COUNTRY_FILTER_MODES.CUSTOM) {
            lastCustomCodesRef.current = nextCodes;
        }
        dispatchCountryFilterUpdate(
            nextMode !== COUNTRY_FILTER_MODES.ANY && nextCodes.length > 0,
            nextCodes,
        );
    };

    const handleModeChange = (nextMode) => {
        const currentDisplay =
            mode !== COUNTRY_FILTER_MODES.ANY &&
            isSameRegionSelection(selectedCodes, countries)
                ? COUNTRY_FILTER_MODES.REGION
                : mode;
        if (isMatchmakingEditingDisabled || nextMode === currentDisplay) {
            return;
        }
        if (mode === COUNTRY_FILTER_MODES.CUSTOM) {
            lastCustomCodesRef.current = selectedCodes;
        }
        if (nextMode === COUNTRY_FILTER_MODES.ANY) {
            applyCodes(nextMode, []);
            return;
        }
        if (nextMode === COUNTRY_FILTER_MODES.REGION) {
            applyCodes(nextMode, regionCodes);
            return;
        }
        const restored = lastCustomCodesRef.current.filter(Boolean);
        applyCodes(nextMode, sameCodeSet(restored, regionCodes) ? [] : restored);
    };

    const handleCountryCardToggle = (code2) => {
        if (isMatchmakingEditingDisabled) return;
        const isSelected = selectedCodes.includes(code2);
        const newCodes = isSelected
            ? selectedCodes.filter((c) => c !== code2)
            : [...selectedCodes, code2];
        applyCodes(COUNTRY_FILTER_MODES.CUSTOM, newCodes);
    };

    const handleClearCodes = (codesToRemove) => {
        if (isMatchmakingEditingDisabled) return;
        const remove = new Set(codesToRemove);
        applyCodes(
            COUNTRY_FILTER_MODES.CUSTOM,
            selectedCodes.filter((code) => !remove.has(code)),
        );
    };

    const continentGroups = useMemo(
        () => groupCountriesByContinent(countries),
        [countries],
    );
    const searchQuery = countrySearch.trim().toLowerCase();
    const visibleGroups = useMemo(() => {
        if (!searchQuery) {
            return continentGroups;
        }
        return continentGroups
            .map((group) => ({
                ...group,
                countries: group.countries.filter(
                    (country) =>
                        country.displayName.toLowerCase().includes(searchQuery) ||
                        String(country.code2 || "")
                            .toLowerCase()
                            .includes(searchQuery),
                ),
            }))
            .filter((group) => group.countries.length > 0);
    }, [continentGroups, searchQuery]);

    useEffect(() => {
        if (continentGroups.length === 0) {
            return;
        }
        setExpandedContinents((current) => {
            if (Object.keys(current).length > 0) {
                return current;
            }
            const selectedSet = new Set(selectedCodes);
            const withSelection = continentGroups.find((group) =>
                group.countries.some((country) => selectedSet.has(country.code2)),
            );
            const initialId = withSelection?.id || continentGroups[0].id;
            return { [initialId]: true };
        });
    }, [continentGroups, selectedCodes]);

    const selectedCountries = useMemo(
        () =>
            selectedCodes
                .map((code) =>
                    countries.find((country) => country.code2 === code),
                )
                .filter(Boolean),
        [selectedCodes, countries],
    );

    const playerCountryInfo = useMemo(
        () => resolvePlayerCountryPresentation(playerCountry, countries),
        [playerCountry, countries],
    );
    const countryModeOptions = useMemo(
        () =>
            COUNTRY_FILTER_MODE_OPTIONS.map((option) => {
                if (
                    option.value !== COUNTRY_FILTER_MODES.REGION ||
                    !playerCountryInfo?.flagImageUrl
                ) {
                    return option;
                }
                return {
                    ...option,
                    icon: (
                        <Box
                            component="img"
                            src={playerCountryInfo.flagImageUrl}
                            alt=""
                            sx={{
                                width: 16,
                                height: 12,
                                borderRadius: "2px",
                                objectFit: "cover",
                                border: "1px solid rgba(255,255,255,0.16)",
                                display: "block",
                            }}
                        />
                    ),
                };
            }),
        [playerCountryInfo],
    );

    const displayMode =
        mode !== COUNTRY_FILTER_MODES.ANY &&
        isSameRegionSelection(selectedCodes, countries)
            ? COUNTRY_FILTER_MODES.REGION
            : mode;
    const showCustomPanel = displayMode === COUNTRY_FILTER_MODES.CUSTOM;
    const showPremiumGate =
        shouldShowPremiumMatchFiltersPrompt({
            membershipTier,
            currentLobbyCode,
            canEditLobbySettings,
        }) && !hidePremiumOverlay;
    const descriptionText =
        displayMode === COUNTRY_FILTER_MODES.ANY
            ? "Match with players from any country."
            : displayMode === COUNTRY_FILTER_MODES.REGION
              ? playerCountryInfo?.displayName
                  ? `Match with players from your region (${playerCountryInfo.displayName}).`
                  : "Match with players from your region."
              : selectedCodes.length > 0
                ? `Matching only with players from ${selectedCodes.length} selected countr${selectedCodes.length === 1 ? "y" : "ies"}.`
                : "Choose specific countries to stay within.";

    return (
        <Box sx={{ position: "relative" }}>
            <Box
                sx={{
                    ...SETTINGS_SECTION_PAD_SX,
                    ...(showPremiumGate
                        ? {
                              opacity: 0.4,
                              pointerEvents: "none",
                              userSelect: "none",
                          }
                        : undefined),
                }}
            >
                <Box sx={SETTINGS_CONTENT_COLUMN_SX}>
                    <Box sx={SETTINGS_SECTION_HEADER_SX}>
                        <Typography sx={SETTINGS_SECTION_TITLE_SX}>
                            Country filters
                        </Typography>
                        <Typography sx={SETTINGS_SECTION_DESC_SX}>
                            {descriptionText}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            mt: "1.1rem",
                            [MOBILE_NAV_MQ]: { mt: "0.75rem" },
                        }}
                    >
                        <MMSegmentedControl
                            options={countryModeOptions}
                            value={displayMode}
                            disabled={isMatchmakingEditingDisabled}
                            ariaLabel="Country filter mode"
                            onChange={handleModeChange}
                        />
                        <Typography
                            sx={{
                                mt: "0.55rem",
                                fontSize: "0.68rem",
                                color: "rgba(255,255,255,0.38)",
                                lineHeight: 1.4,
                            }}
                        >
                            This filter only affects other players in your match, not
                            the game server location.
                        </Typography>
                    </Box>

                    {showCustomPanel ? (
                        <Box
                            sx={{
                                mt: "1.15rem",
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.85rem",
                            }}
                        >
                            {selectedCountries.length > 0 ? (
                                <Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: "0.45rem",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "0.68rem",
                                                fontWeight: 700,
                                                letterSpacing: "0.12em",
                                                textTransform: "uppercase",
                                                color: "rgba(255,255,255,0.45)",
                                            }}
                                        >
                                            Selected ({selectedCountries.length})
                                        </Typography>
                                        <ButtonBase
                                            disableRipple
                                            disabled={isMatchmakingEditingDisabled}
                                            onClick={() => handleClearCodes(selectedCodes)}
                                            sx={{
                                                fontSize: "0.68rem",
                                                fontWeight: 600,
                                                letterSpacing: "0.06em",
                                                textTransform: "uppercase",
                                                color: "rgba(255,255,255,0.5)",
                                                py: "0.15rem",
                                                "&:hover": {
                                                    color: colors.text.primary,
                                                },
                                            }}
                                        >
                                            Clear
                                        </ButtonBase>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "0.4rem",
                                        }}
                                    >
                                        {selectedCountries.map((country) => (
                                            <Box
                                                key={country.code2}
                                                sx={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "0.4rem",
                                                    pl: "0.4rem",
                                                    pr: "0.25rem",
                                                    py: "0.28rem",
                                                    borderRadius: "999px",
                                                    background: "rgba(255,255,255,0.05)",
                                                    border: "1px solid rgba(255,255,255,0.08)",
                                                }}
                                            >
                                                <CountryFlag src={country.flagImageUrl} />
                                                <Typography
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        fontWeight: 600,
                                                        color: colors.text.primary,
                                                    }}
                                                >
                                                    {country.displayName}
                                                </Typography>
                                                <ButtonBase
                                                    disableRipple
                                                    aria-label={`Remove ${country.displayName}`}
                                                    disabled={isMatchmakingEditingDisabled}
                                                    onClick={() =>
                                                        handleCountryCardToggle(country.code2)
                                                    }
                                                    sx={{
                                                        width: "1.15rem",
                                                        height: "1.15rem",
                                                        borderRadius: "50%",
                                                        color: "rgba(255,255,255,0.55)",
                                                        "&:hover": {
                                                            color: colors.text.primary,
                                                            background: "rgba(255,255,255,0.08)",
                                                        },
                                                    }}
                                                >
                                                    <Close sx={{ fontSize: "0.85rem" }} />
                                                </ButtonBase>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            ) : null}

                            <TextField
                                placeholder="Search countries..."
                                value={countrySearch}
                                onChange={(e) =>
                                    setCountrySearch(
                                        e.target.value.slice(0, COUNTRY_SEARCH_MAX_CHARS),
                                    )
                                }
                                disabled={isMatchmakingEditingDisabled}
                                sx={countrySearchFieldSx}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ fontSize: "1.15rem" }} />
                                            </InputAdornment>
                                        ),
                                    },
                                    htmlInput: {
                                        maxLength: COUNTRY_SEARCH_MAX_CHARS,
                                    },
                                }}
                            />

                            {countriesLoading ? (
                                <Box sx={{ display: "grid", gap: "0.4rem" }}>
                                    {Array.from({ length: COUNTRY_SKELETON_COUNT }).map(
                                        (_, index) => (
                                            <CountryFilterRowSkeleton
                                                key={`country-skeleton-${index}`}
                                            />
                                        ),
                                    )}
                                </Box>
                            ) : visibleGroups.length === 0 ? (
                                <SearchEmptyState
                                    title="No countries found"
                                    description="Try a different name or check your spelling."
                                    searchQuery={countrySearch}
                                    onClearSearch={() => setCountrySearch("")}
                                />
                            ) : (
                                <Box sx={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                                    {visibleGroups.map((group) => {
                                        const groupCodes = group.countries.map(
                                            (country) => country.code2,
                                        );
                                        const selectedInGroup = groupCodes.filter((code) =>
                                            selectedCodes.includes(code),
                                        ).length;
                                        const expanded =
                                            Boolean(searchQuery) ||
                                            Boolean(expandedContinents[group.id]);

                                        return (
                                            <Box
                                                key={group.id}
                                                sx={{
                                                    border: "1px solid rgba(255,255,255,0.07)",
                                                    borderRadius: "10px",
                                                    overflow: "hidden",
                                                    background: "rgba(255,255,255,0.02)",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.5rem",
                                                        px: "0.8rem",
                                                        minHeight: "2.7rem",
                                                    }}
                                                >
                                                    <ButtonBase
                                                        disableRipple
                                                        onClick={() =>
                                                            setExpandedContinents((current) => ({
                                                                ...current,
                                                                [group.id]: !current[group.id],
                                                            }))
                                                        }
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            gap: "0.75rem",
                                                            py: "0.55rem",
                                                            textAlign: "left",
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.72rem",
                                                                fontWeight: 800,
                                                                letterSpacing: "0.14em",
                                                                textTransform: "uppercase",
                                                                color: colors.text.primary,
                                                            }}
                                                        >
                                                            {group.label}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "0.45rem",
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: "0.65rem",
                                                                    fontWeight: 600,
                                                                    color: "rgba(255,255,255,0.42)",
                                                                }}
                                                            >
                                                                {selectedInGroup} selected
                                                            </Typography>
                                                            <ExpandMore
                                                                sx={{
                                                                    fontSize: "1.2rem",
                                                                    color: "rgba(255,255,255,0.45)",
                                                                    transform: expanded
                                                                        ? "rotate(180deg)"
                                                                        : "rotate(0deg)",
                                                                    transition:
                                                                        "transform 0.15s ease",
                                                                }}
                                                            />
                                                        </Box>
                                                    </ButtonBase>
                                                    {selectedInGroup > 0 ? (
                                                        <ButtonBase
                                                            disableRipple
                                                            disabled={isMatchmakingEditingDisabled}
                                                            onClick={() =>
                                                                handleClearCodes(groupCodes)
                                                            }
                                                            sx={{
                                                                flexShrink: 0,
                                                                fontSize: "0.62rem",
                                                                fontWeight: 700,
                                                                letterSpacing: "0.06em",
                                                                textTransform: "uppercase",
                                                                color: "rgba(255,255,255,0.5)",
                                                                py: "0.2rem",
                                                                "&:hover": {
                                                                    color: colors.text.primary,
                                                                },
                                                            }}
                                                        >
                                                            Clear
                                                        </ButtonBase>
                                                    ) : null}
                                                </Box>
                                                <Collapse in={expanded} timeout={140}>
                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: {
                                                                xs: "1fr",
                                                                sm: "repeat(2, minmax(0, 1fr))",
                                                            },
                                                            gap: "0.35rem",
                                                            px: "0.65rem",
                                                            pb: "0.7rem",
                                                        }}
                                                    >
                                                        {group.countries.map((country) => {
                                                            const isSelected =
                                                                selectedCodes.includes(
                                                                    country.code2,
                                                                );
                                                            return (
                                                                <LobbySelectableRow
                                                                    key={country.code2}
                                                                    checked={isSelected}
                                                                    onClick={() =>
                                                                        handleCountryCardToggle(
                                                                            country.code2,
                                                                        )
                                                                    }
                                                                    interactive={
                                                                        !isMatchmakingEditingDisabled
                                                                    }
                                                                    dimmed={false}
                                                                    unselectedHoverGlow="primary"
                                                                    showCheckbox
                                                                    sx={{
                                                                        gap: "0.45rem",
                                                                        px: "0.55rem",
                                                                        minHeight: "2.75rem",
                                                                        pointerEvents:
                                                                            isMatchmakingEditingDisabled
                                                                                ? "none"
                                                                                : "auto",
                                                                    }}
                                                                    leading={
                                                                        <CountryFlag
                                                                            src={country.flagImageUrl}
                                                                        />
                                                                    }
                                                                    middle={
                                                                        <Typography
                                                                            sx={{
                                                                                color: colors.text.primary,
                                                                                fontSize: "0.86rem",
                                                                                fontWeight: 500,
                                                                                minWidth: 0,
                                                                                whiteSpace: "nowrap",
                                                                                overflow: "hidden",
                                                                                textOverflow: "ellipsis",
                                                                            }}
                                                                        >
                                                                            {country.displayName}
                                                                        </Typography>
                                                                    }
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>
                    ) : null}
                </Box>
            </Box>
            {showPremiumGate ? <PremiumOnlyOverlay /> : null}
        </Box>
    );
}
